import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [nflData, setNflData] = useState([]);
  const [nbaData, setNbaData] = useState([]);
  const [nflStandings, setNflStandings] = useState({});
  const [nbaStandings, setNbaStandings] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchNFLData();
    fetchNBAData();
    fetchNFLStandings();
    fetchNBAStandings();
  }, []);

  const fetchNFLData = async () => {
    try {
      const response = await fetch('/api/nfl/players');
      const data = await response.json();
      setNflData(data.data || []);
    } catch (error) {
      console.error('Error fetching NFL data:', error);
    }
  };

  const fetchNBAData = async () => {
    try {
      const response = await fetch('/api/nba/players');
      const data = await response.json();
      setNbaData(data.data || []);
    } catch (error) {
      console.error('Error fetching NBA data:', error);
    }
  };

  const fetchNFLStandings = async () => {
    try {
      const response = await fetch('/api/nfl/standings');
      const data = await response.json();
      setNflStandings(data.data || {});
    } catch (error) {
      console.error('Error fetching NFL standings:', error);
    }
  };

  const fetchNBAStandings = async () => {
    try {
      const response = await fetch('/api/nba/standings');
      const data = await response.json();
      setNbaStandings(data.data || {});
    } catch (error) {
      console.error('Error fetching NBA standings:', error);
    }
  };

  const renderHomePage = () => (
    <div className="page">
      <div className="main-header">⚡ Super Cool Sports Network</div>
      <div className="sub-header">Your Ultimate Sports Hub 🏈🏀</div>

      <div className="hero-section">
        <h2>Welcome to the Coolest Sports Experience! 🎉</h2>
        <p>Track your favorite NFL and NBA players, check live standings, and stay updated with the latest stats!</p>
      </div>

      <div className="feature-cards">
        <div className="feature-card" onClick={() => setCurrentPage('nfl')}>
          <h2>🏈 NFL</h2>
          <p>Track NFL player statistics, season standings, and playoff action!</p>
          <ul>
            <li>📊 Player stats by position</li>
            <li>🏆 Live conference standings</li>
            <li>🎯 Playoffs & Super Bowl updates</li>
          </ul>
        </div>

        <div className="feature-card" onClick={() => setCurrentPage('nba')}>
          <h2>🏀 NBA</h2>
          <p>Follow NBA stars, check team standings, and dive deep into stats!</p>
          <ul>
            <li>⭐ Top player performances</li>
            <li>📈 Conference standings</li>
            <li>🔥 Position-specific stats</li>
          </ul>
        </div>
      </div>

      <div className="quick-stats">
        <div className="stat-item">
          <h3>🏈 NFL Teams</h3>
          <p>32</p>
        </div>
        <div className="stat-item">
          <h3>🏀 NBA Teams</h3>
          <p>30</p>
        </div>
        <div className="stat-item">
          <h3>📊 Positions</h3>
          <p>11</p>
        </div>
        <div className="stat-item">
          <h3>⚡ Live Data</h3>
          <p>Yes</p>
        </div>
      </div>
    </div>
  );

  const renderNFLPage = () => (
    <div className="page">
      <div className="league-header">🏈 NFL - National Football League</div>
      <div className="page-subtitle">⚡ Live data updates • 📊 Comprehensive player stats • 🏆 Real-time standings</div>

      <div className="tabs">
        <button
          className={currentPage === 'nfl' ? 'tab active' : 'tab'}
          onClick={() => setCurrentPage('nfl')}
        >
          📊 Player Stats
        </button>
        <button
          className={currentPage === 'nfl-standings' ? 'tab active' : 'tab'}
          onClick={() => setCurrentPage('nfl-standings')}
        >
          🏆 Standings
        </button>
      </div>

      {currentPage === 'nfl' && (
        <div className="data-section">
          <h3>NFL Player Stats</h3>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Team</th>
                  <th>Position</th>
                  <th>Pass Yds</th>
                  <th>Rush Yds</th>
                  <th>Rec Yds</th>
                  <th>Tackles</th>
                  <th>INT</th>
                </tr>
              </thead>
              <tbody>
                {nflData.slice(0, 20).map((player, index) => (
                  <tr key={index}>
                    <td>{player.Name}</td>
                    <td>{player.Team}</td>
                    <td>{player.Position}</td>
                    <td>{player['Pass Yds'] || '-'}</td>
                    <td>{player['Rush Yds'] || '-'}</td>
                    <td>{player['Rec Yds'] || '-'}</td>
                    <td>{player.Tackles || '-'}</td>
                    <td>{player.INT || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {currentPage === 'nfl-standings' && (
        <div className="data-section">
          <h3>NFL Conference Standings</h3>
          <div className="standings">
            <div className="conference">
              <h4>AFC Standings</h4>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Wins</th>
                      <th>Losses</th>
                      <th>Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(nflStandings.AFC || []).map((team, index) => (
                      <tr key={index}>
                        <td>{team.Team}</td>
                        <td>{team.Wins}</td>
                        <td>{team.Losses}</td>
                        <td>{team['Win %']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="conference">
              <h4>NFC Standings</h4>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Wins</th>
                      <th>Losses</th>
                      <th>Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(nflStandings.NFC || []).map((team, index) => (
                      <tr key={index}>
                        <td>{team.Team}</td>
                        <td>{team.Wins}</td>
                        <td>{team.Losses}</td>
                        <td>{team['Win %']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderNBAPage = () => (
    <div className="page">
      <div className="league-header">🏀 NBA - National Basketball Association</div>
      <div className="page-subtitle">⚡ Live data updates • 📊 Comprehensive player stats • 🏆 Real-time standings</div>

      <div className="tabs">
        <button
          className={currentPage === 'nba' ? 'tab active' : 'tab'}
          onClick={() => setCurrentPage('nba')}
        >
          📊 Player Stats
        </button>
        <button
          className={currentPage === 'nba-standings' ? 'tab active' : 'tab'}
          onClick={() => setCurrentPage('nba-standings')}
        >
          🏆 Standings
        </button>
      </div>

      {currentPage === 'nba' && (
        <div className="data-section">
          <h3>NBA Player Stats</h3>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Team</th>
                  <th>Position</th>
                  <th>PPG</th>
                  <th>RPG</th>
                  <th>APG</th>
                  <th>FG%</th>
                  <th>3P%</th>
                </tr>
              </thead>
              <tbody>
                {nbaData.map((player, index) => (
                  <tr key={index}>
                    <td>{player.Name}</td>
                    <td>{player.Team}</td>
                    <td>{player.Position}</td>
                    <td>{player.PPG}</td>
                    <td>{player.RPG}</td>
                    <td>{player.APG}</td>
                    <td>{player['FG%']}%</td>
                    <td>{player['3P%']}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {currentPage === 'nba-standings' && (
        <div className="data-section">
          <h3>NBA Conference Standings</h3>
          <div className="standings">
            <div className="conference">
              <h4>Eastern Conference</h4>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Wins</th>
                      <th>Losses</th>
                      <th>Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(nbaStandings.Eastern || []).map((team, index) => (
                      <tr key={index}>
                        <td>{team.Team}</td>
                        <td>{team.Wins}</td>
                        <td>{team.Losses}</td>
                        <td>{team['Win %']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="conference">
              <h4>Western Conference</h4>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Wins</th>
                      <th>Losses</th>
                      <th>Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(nbaStandings.Western || []).map((team, index) => (
                      <tr key={index}>
                        <td>{team.Team}</td>
                        <td>{team.Wins}</td>
                        <td>{team.Losses}</td>
                        <td>{team['Win %']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => setCurrentPage('home')}>
          ⚡ SCSN
        </div>
        <div className="nav-links">
          <button
            className={currentPage === 'home' ? 'nav-link active' : 'nav-link'}
            onClick={() => setCurrentPage('home')}
          >
            🏠 Home
          </button>
          <button
            className={currentPage.startsWith('nfl') ? 'nav-link active' : 'nav-link'}
            onClick={() => setCurrentPage('nfl')}
          >
            🏈 NFL
          </button>
          <button
            className={currentPage.startsWith('nba') ? 'nav-link active' : 'nav-link'}
            onClick={() => setCurrentPage('nba')}
          >
            🏀 NBA
          </button>
        </div>
      </nav>

      <main className="main-content">
        {currentPage === 'home' && renderHomePage()}
        {currentPage.startsWith('nfl') && renderNFLPage()}
        {currentPage.startsWith('nba') && renderNBAPage()}
      </main>
    </div>
  );
}

export default App;
